import React, { useEffect, useRef, useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";
import { GlobeAltIcon, PhoneIcon } from "@heroicons/react/24/outline";
import WhatsAppIcon from "./WhatsAppIcon";

export type AdCardMedia = { type: "image" | "video"; url: string };

export interface AdvertisementCardProps {
    companyName: string;
    media: AdCardMedia[];
    shortDescription?: string;
    whatsappNumber?: string;
    mobileNumber?: string;
    websiteLink?: string;
}

const initialOf = (name: string) => (name?.trim()?.charAt(0) || "?").toUpperCase();

// Deterministic so the same company always gets the same avatar color, instead
// of a random one that'd flicker between renders/pages for the same ad.
const AVATAR_PALETTE = ["#264164", "#7c3aed", "#0f766e", "#b45309", "#be123c", "#1d4ed8"];
const avatarColorFor = (name: string) => {
    let hash = 0;
    for (let i = 0; i < name.length; i++) hash = (hash * 31 + name.charCodeAt(i)) >>> 0;
    return AVATAR_PALETTE[hash % AVATAR_PALETTE.length];
};

// Renders an advertisement the way it actually appears in the app feed — avatar
// + "Sponsored"/AD badge header, media carousel, WhatsApp/Learn More/Call Now
// buttons, and the short description as the caption line underneath. Shared by
// the admin Add, Edit, and View pages so all three show the exact same thing
// instead of three copies that can drift out of sync.
const AdvertisementCard: React.FC<AdvertisementCardProps> = ({
    companyName,
    media,
    shortDescription,
    whatsappNumber,
    mobileNumber,
    websiteLink,
}) => {
    const [activeIndex, setActiveIndex] = useState(0);
    const [isMuted, setIsMuted] = useState(true);
    const [videoHasAudio, setVideoHasAudio] = useState<Record<number, boolean>>({});
    const videoRefs = useRef<Record<number, HTMLVideoElement | null>>({});

    useEffect(() => {
        // Media list changed (e.g. a file was added/removed) — the previously
        // active slide index may no longer exist.
        if (activeIndex >= media.length) setActiveIndex(0);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [media.length]);

    const checkVideoAudio = (video: HTMLVideoElement, index: number) => {
        try {
            const videoAny = video as any;
            let hasAudio = false;
            if (typeof videoAny.captureStream === "function") {
                hasAudio = videoAny.captureStream().getAudioTracks().length > 0;
            } else if (typeof videoAny.mozCaptureStream === "function") {
                hasAudio = videoAny.mozCaptureStream().getAudioTracks().length > 0;
            }
            setVideoHasAudio((prev) => ({ ...prev, [index]: hasAudio }));
        } catch {
            setVideoHasAudio((prev) => ({ ...prev, [index]: false }));
        }
    };

    const toggleMute = () => {
        const activeVideo = videoRefs.current[activeIndex];
        if (!activeVideo) return;
        const newMuteState = !activeVideo.muted;
        // Mute every video first, then unmute only the active one — otherwise
        // a slide swipe could leave a previous slide's video quietly unmuted.
        Object.values(videoRefs.current).forEach((video) => {
            if (video) video.muted = true;
        });
        activeVideo.muted = newMuteState;
        setIsMuted(newMuteState);
    };

    const togglePlay = (index: number) => {
        const video = videoRefs.current[index];
        if (!video) return;
        if (video.paused) video.play();
        else video.pause();
    };

    // All three CTAs are now derived purely from whether the ad actually has
    // that contact detail — no more per-slide visibility configuration.
    const showWhatsapp = !!whatsappNumber;
    const showWebsite = !!websiteLink;
    const showCall = !!mobileNumber;
    const hasAnyCta = showWhatsapp || showWebsite || showCall;

    // Nothing to preview yet — a plain full-height placeholder (matching the
    // phone frame's own black bezel) reads cleaner than showing the white
    // header card above an empty gap, which is all the frame has beneath it
    // when there's no media, no CTA row, and no caption to fill it out.
    if (media.length === 0) {
        return (
            <div className="w-full h-full flex items-center justify-center text-center px-6" style={{ minHeight: "100%", background: "#171717" }}>
                <p className="text-white text-lg m-0">Upload media to see the preview</p>
            </div>
        );
    }

    // flex flex-col + the media area as the one flex-growing (AND shrinking —
    // minHeight:0 below) child: h-full (not min-h-full) gives the card a
    // DEFINITE height to divide up, so the media area reliably absorbs
    // exactly whatever the header/CTA/caption don't use — filling the frame
    // with real content instead of blank space when short, and shrinking
    // itself (never the frame) when that other content runs long, so the
    // card can never scroll or overflow the phone-frame mockup either way.
    return (
        <div className="w-full h-full bg-white flex flex-col">
            {/* Header: avatar + name + Sponsored + AD badge */}
            <div className="flex items-center gap-3 px-4 pt-4 pb-3 flex-shrink-0">
                <div
                    className="flex items-center justify-center rounded-full flex-shrink-0"
                    style={{ width: 44, height: 44, background: avatarColorFor(companyName || "?"), color: "#fff", fontWeight: 700, fontSize: "18px" }}
                >
                    {initialOf(companyName || "?")}
                </div>
                <div className="min-w-0">
                    <p className="m-0 font-bold text-[15px] truncate" style={{ color: "#111827" }}>
                        {companyName || "Your Brand Name"}
                    </p>
                    <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-xs" style={{ color: "#9ca3af" }}>Sponsored</span>
                        <span
                            className="text-[10px] font-bold px-1.5 py-0.5 rounded"
                            style={{ background: "#ef4444", color: "#fff", letterSpacing: "0.03em" }}
                        >
                            AD
                        </span>
                    </div>
                </div>
            </div>

            {/* Media — media.length === 0 already returned early above. Flexes
                to fill whatever space the header/CTA/caption don't use — every
                slide still occupies that same, now-flexible area regardless of
                that image's own aspect ratio, which is what makes the
                blurred-fill letterboxing below read as a single consistent
                frame instead of jumping size per slide. minHeight:0 is
                required for a flex child to actually shrink/grow inside a
                column flex container instead of defaulting to content size. */}
            <div className="relative w-full bg-black" style={{ flex: "1 1 auto", minHeight: 0 }}>
                <Swiper
                    modules={[Navigation, Pagination]}
                    slidesPerView={1}
                    pagination={media.length > 1 ? { clickable: true } : false}
                    onSlideChange={(swiper: any) => {
                        const newIndex = swiper.realIndex;
                        setActiveIndex(newIndex);
                        Object.entries(videoRefs.current).forEach(([index, video]) => {
                            if (!video) return;
                            if (Number(index) === newIndex) {
                                video.play();
                                video.muted = true;
                            } else {
                                video.pause();
                                video.muted = true;
                            }
                        });
                        setIsMuted(true);
                    }}
                    style={{
                        // Fills its now-flexible parent (see the wrapper div above)
                        // instead of a fixed px height, so it grows/shrinks with it.
                        height: "100%",
                        ["--swiper-pagination-color" as any]: "#ffffff",
                        ["--swiper-pagination-bullet-inactive-color" as any]: "#ffffff",
                        ["--swiper-pagination-bullet-inactive-opacity" as any]: 0.5,
                    }}
                >
                    {media.map((item, index) => (
                        <SwiperSlide key={index} className="relative h-full w-full bg-black overflow-hidden">
                            {item.type === "image" ? (
                                <>
                                    {/* Blurred, scaled-up copy of the same image fills the
                                        frame behind it — avoids plain black letterbox bars
                                        when the image's own aspect ratio doesn't match the
                                        frame, matching how the app itself renders this. */}
                                    <div
                                        className="absolute inset-0"
                                        style={{
                                            backgroundImage: `url(${item.url})`,
                                            backgroundSize: "cover",
                                            backgroundPosition: "center",
                                            filter: "blur(20px)",
                                            transform: "scale(1.15)",
                                        }}
                                    />
                                    <img
                                        src={item.url}
                                        alt={companyName}
                                        className="relative z-10 w-full h-full object-contain"
                                        draggable={false}
                                    />
                                </>
                            ) : (
                                <>
                                    <video
                                        ref={(el) => {
                                            videoRefs.current[index] = el;
                                        }}
                                        src={item.url}
                                        muted
                                        loop
                                        autoPlay
                                        playsInline
                                        onLoadedMetadata={(e) => checkVideoAudio(e.currentTarget, index)}
                                        onClick={() => togglePlay(index)}
                                        className="relative z-10 w-full h-full object-contain cursor-pointer"
                                    />
                                    {videoHasAudio[index] && (
                                        <div
                                            onClick={toggleMute}
                                            className="absolute bottom-3 right-3 bg-black bg-opacity-50 text-white px-3 py-1 rounded-full cursor-pointer z-30"
                                        >
                                            {isMuted ? "🔇" : "🔊"}
                                        </div>
                                    )}
                                </>
                            )}
                        </SwiperSlide>
                    ))}
                </Swiper>
            </div>

            {/* CTA row */}
            {hasAnyCta && (
                <div className="flex items-center gap-2 px-4 pt-3 flex-wrap flex-shrink-0">
                    {showWhatsapp && (
                        <a
                            href={`https://wa.me/${(whatsappNumber || "").replace(/\D/g, "")}`}
                            target="_blank"
                            rel="noreferrer"
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-semibold flex-1 justify-center"
                            style={{ background: "#25D366", color: "#fff", textDecoration: "none" }}
                        >
                            <WhatsAppIcon size={14} /> WhatsApp
                        </a>
                    )}
                    {showWebsite && (
                        <a
                            href={websiteLink?.startsWith("http") ? websiteLink : `https://${websiteLink}`}
                            target="_blank"
                            rel="noreferrer"
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-semibold flex-1 justify-center"
                            style={{ border: "1.5px solid #264164", color: "#264164", textDecoration: "none" }}
                        >
                            <GlobeAltIcon className="w-4 h-4" /> Learn more
                        </a>
                    )}
                    {showCall && (
                        <a
                            href={`tel:${(mobileNumber || "").replace(/\D/g, "")}`}
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-semibold flex-1 justify-center"
                            style={{ border: "1.5px solid #264164", color: "#264164", textDecoration: "none" }}
                        >
                            <PhoneIcon className="w-4 h-4" /> Call Now
                        </a>
                    )}
                </div>
            )}

            {/* Caption / category line */}
            {shortDescription && (
                <p className="m-0 px-4 pt-3 pb-4 text-sm flex-shrink-0" style={{ color: "#4b5563" }}>
                    {shortDescription}
                </p>
            )}
            {!shortDescription && <div className="pb-2 flex-shrink-0" />}
        </div>
    );
};

export default AdvertisementCard;
